import React from 'react';
import { meta } from '@/lib/lifecycle';
import { Star, AlertTriangle, Home, Video } from 'lucide-react';
import { CAL } from '@/constants/testIds';
import { cn } from '@/lib/utils';

/** Compact appointment chip for calendar cells. */
export default function AppointmentChip({
  apt, patient, aptType, employee, onClick, style, compact = false, draggable = true,
  onDragStart,
}) {
  const m = meta(apt.state);
  const Icon = m.icon;

  const isVip = apt.priority === 'vip';
  const isEmergency = apt.priority === 'emergency';
  const isHome = apt.channel === 'home_visit';
  const isTele = apt.channel === 'teleconsultation';

  const time = new Date(apt.start_ts).toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit',
  });

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClick && onClick(apt.id); }}
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) return;
        e.dataTransfer.setData('text/plain', apt.id);
        e.dataTransfer.effectAllowed = 'move';
        e.currentTarget.classList.add('dragging');
        onDragStart && onDragStart(apt);
      }}
      onDragEnd={(e) => e.currentTarget.classList.remove('dragging')}
      style={{
        backgroundColor: m.bg,
        borderColor: m.border,
        borderLeftColor: m.text,
        color: m.text,
        ...style,
      }}
      data-testid={CAL.appointmentChip(apt.id)}
      className={cn(
        'hc-appt-chip cursor-pointer border rounded-sm px-1.5 py-1 text-[11px] leading-tight overflow-hidden',
        'border-l-[4px] hover:brightness-95',
        isEmergency && 'ring-2 ring-red-500 ring-offset-1',
        isHome && 'border-dashed',
        isTele && 'border-dotted'
      )}
      title={`${patient?.full_name || 'Patient'} — ${m.label}`}
    >
      <div className="flex items-center gap-1 font-semibold">
        <Icon className="w-3 h-3 shrink-0" />
        <span className="tabular-nums">{time}</span>
        {isVip && <Star className="w-3 h-3 fill-current ml-auto shrink-0" />}
        {isEmergency && <AlertTriangle className="w-3 h-3 shrink-0 ml-auto" />}
        {isHome && !compact && <Home className="w-3 h-3 shrink-0" />}
        {isTele && !compact && <Video className="w-3 h-3 shrink-0" />}
      </div>
      <div className="font-medium text-stone-900 truncate">
        {patient?.full_name || apt?.patient_name || 'Unknown patient'}
      </div>
      {!compact && (
        <div className="opacity-75 truncate">
          {aptType?.name || '—'}
          {employee ? ` · ${employee.full_name}` : ''}
        </div>
      )}
      <div className="text-[9px] uppercase tracking-wider font-semibold mt-0.5 opacity-80">
        {m.label}
      </div>
    </div>
  );
}
